package fr.xibalba.generateurClassesLycee.components.layouts

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.*
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.PageContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toModifier
import fr.xibalba.generateurClassesLycee.GlobalData
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.css.AlignItems

enum class State(val color: CSSColorValue) {
    SUCCESS(rgb(34, 197, 94)),     // Green
    IDLE(rgb(156, 163, 175)),      // Gray
    ERROR(rgb(239, 68, 68))        // Red
}

val navbarSections = listOf(
    Pair("Fichier", listOf("Chargement", "Nettoyage")),
    Pair("Eleves", listOf("Liste")),
    Pair("Agencement", listOf("Niveau", "Paramétrage", "Répartition")),
    Pair("Bilan", listOf("Statistiques", "Export"))
)

private var activeSection by mutableStateOf(navbarSections.first().first)
private var activeSubSection by mutableStateOf<String?>(null)

fun getState(sectionName: String): State {
    if (!sectionName.contains("/")) {
        return getTopLevelState(sectionName)
    }
    return when (sectionName) {
        "Fichier/Chargement" -> when (GlobalData.fichier) {
            null -> State.ERROR
            else -> State.SUCCESS
        }
        "Fichier/Nettoyage" -> when (GlobalData.loadedFichier?.errorLines?.size) {
            0 -> State.SUCCESS
            null -> State.IDLE
            else -> State.ERROR
        }
        "Eleves/Liste" -> when (GlobalData.loadedFichier?.goodEleves?.size) {
            null -> State.IDLE
            else -> State.SUCCESS
        }
        else -> State.IDLE
    }
}

fun getTopLevelState(topLevelName: String): State {
    return navbarSections.find { it.first == topLevelName }!!.second.maxOf { getState("$topLevelName/$it") }
}

// Primary navbar style (top level)
val PrimaryNavbarStyle = CssStyle {
    base {
        Modifier
            .width(100.percent)
            .height(48.px)
            .backgroundColor(rgb(248, 249, 250))
            .borderBottom(1.px, LineStyle.Solid, rgb(220, 223, 227))
            .display(DisplayStyle.Flex)
            .alignItems(AlignItems.Center)
            .padding(leftRight = 16.px)
            .boxShadow(0.px, 1.px, 3.px, 0.px, rgba(0, 0, 0, 0.1))
    }
}

// Secondary navbar style (second level)
val SecondaryNavbarStyle = CssStyle {
    base {
        Modifier
            .width(100.percent)
            .height(40.px)
            .backgroundColor(Colors.White)
            .borderBottom(1.px, LineStyle.Solid, rgb(230, 233, 237))
            .display(DisplayStyle.Flex)
            .alignItems(AlignItems.Center)
            .padding(leftRight = 24.px)
    }
}

// Primary navbar button style
val PrimaryButtonStyle = CssStyle {
    base {
        Modifier
            .padding(8.px, 12.px)
            .margin(right = 4.px)
            .backgroundColor(Colors.White)
            .border(1.px, LineStyle.Solid, rgb(220, 223, 227))
            .borderRadius(6.px)
            .cursor(Cursor.Pointer)
            .color(rgb(55, 65, 81))
            .fontSize(14.px)
            .fontWeight(FontWeight.Medium)
            .transition(Transition.of(property = "all", duration = 200.ms))
            .boxShadow(0.px, 1.px, 2.px, 0.px, rgba(0, 0, 0, 0.05))
    }

    hover {
        Modifier
            .backgroundColor(rgb(243, 244, 246))
            .border(1.px, LineStyle.Solid, rgb(209, 213, 219))
            .boxShadow(0.px, 2.px, 4.px, 0.px, rgba(0, 0, 0, 0.1))
    }
}

// Secondary navbar button style
val SecondaryButtonStyle = CssStyle {
    base {
        Modifier
            .padding(6.px, 12.px)
            .margin(right = 8.px)
            .backgroundColor(Colors.White)
            .border(1.px, LineStyle.Solid, rgb(229, 231, 235))
            .borderRadius(4.px)
            .cursor(Cursor.Pointer)
            .color(rgb(75, 85, 99))
            .fontSize(13.px)
            .fontWeight(FontWeight.Normal)
            .transition(Transition.of(property = "all", duration = 150.ms))
    }

    hover {
        Modifier
            .backgroundColor(rgb(249, 250, 251))
            .border(1.px, LineStyle.Solid, rgb(209, 213, 219))
    }
}

// Logo/Brand style
val BrandStyle = CssStyle {
    base {
        Modifier
            .margin(right = 32.px)
            .fontWeight(FontWeight.Bold)
            .fontSize(16.px)
            .color(rgb(17, 24, 39))
    }
}

@Layout
@Composable
fun Navbar(ctx: PageContext, content: @Composable () -> Unit) {
    val pathSegments = ctx.route.path.trim('/').split('/')
    LaunchedEffect(ctx.route.path) {
        val sectionFromUrl = pathSegments.getOrNull(0)?.replaceFirstChar { it.uppercase() }
        if (sectionFromUrl != null && navbarSections.any { it.first.equals(sectionFromUrl, ignoreCase = true) }) {
            activeSection = sectionFromUrl
            val subSectionFromUrl = pathSegments.getOrNull(1)?.replaceFirstChar { it.uppercase() }
            val subSections = navbarSections.find { it.first.equals(sectionFromUrl, ignoreCase = true) }?.second
            if (subSectionFromUrl != null && subSections?.any { it.equals(subSectionFromUrl, ignoreCase = true) } == true) {
                activeSubSection = subSectionFromUrl
            } else {
                activeSubSection = subSections?.firstOrNull()
            }
        }
    }

    Column(Modifier.fillMaxWidth()) {
        // Primary navbar
        Box(PrimaryNavbarStyle.toModifier()) {
            Row(
                Modifier.fillMaxWidth().height(100.percent),
                horizontalArrangement = Arrangement.Start,
                verticalAlignment = Alignment.CenterVertically
            ) {
                // Logo or brand name
                SpanText(
                    "Générateur Classes Lycée",
                    BrandStyle.toModifier()
                )

                // Primary navigation buttons
                navbarSections.forEach { (section, _) ->
                    val sectionState = getState(section)

                    Box(
                        PrimaryButtonStyle.toModifier()
                            .position(Position.Relative)
                            .onClick {
                                activeSection = section
                                activeSubSection = navbarSections.find { it.first == section }!!.second.firstOrNull()
                                if (activeSubSection == null) {
                                    ctx.router.navigateTo("/${section.lowercase()}")
                                }
                            }
                    ) {
                        SpanText(section)

                        // State indicator line at bottom with fading ends
                        Box(
                            Modifier
                                .position(Position.Absolute)
                                .bottom((-1).px)
                                .left(0.px)
                                .right(12.px)
                                .height(3.px)
                                .styleModifier {
                                    property(
                                        "background",
                                        "radial-gradient(ellipse at center, ${sectionState.color} 0%, transparent 70%)"
                                    )
                                }
                        )
                    }
                }
            }
        }

        // Secondary navbar (only shown when a section is active)
        val subsections = navbarSections.find { it.first == activeSection }?.second ?: emptyList()
        if (subsections.isNotEmpty()) {
            Box(SecondaryNavbarStyle.toModifier()) {
                Row(
                    Modifier.fillMaxWidth().height(100.percent),
                    horizontalArrangement = Arrangement.Start,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    subsections.forEach { subsection ->
                        val subsectionState = getState("$activeSection/$subsection")

                        Box(
                            SecondaryButtonStyle.toModifier()
                                .position(Position.Relative)
                                .onClick {
                                    activeSubSection = subsection
                                    ctx.router.navigateTo("/${activeSection.lowercase()}/${subsection.lowercase()}")
                                }
                        ) {
                            SpanText(subsection)

                            // State indicator line at bottom with fading ends (smaller for sub-sections)
                            Box(
                                Modifier
                                    .position(Position.Absolute)
                                    .bottom((-1).px)
                                    .left(0.px)
                                    .right(12.px)
                                    .height(3.px)
                                    .styleModifier {
                                        property(
                                            "background",
                                            "radial-gradient(ellipse at center, ${subsectionState.color} 0%, transparent 70%)"
                                        )
                                    }
                            )
                        }
                    }
                }
            }
        }
    }

    content()
}